import React, { useEffect, useRef } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { TeamInvitation } from '@/components/company/TeamInvitation';
import { TeamMembersList } from '@/components/company/TeamMembersList';
import { CompanySelector } from '@/components/company/CompanySelector';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users } from 'lucide-react';
import { PendingInvitationsList } from '@/components/company/PendingInvitationsList';
import { useCompany } from '@/context/CompanyContext';
import { useActivityLog } from '@/hooks/useActivityLog';

export const TeamManagement: React.FC = () => {
  const { currentCompany, members, fetchMembers, userRole, invitations, fetchInvitations } = useCompany();
  const { logActivity } = useActivityLog();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (hasLoggedRef.current) return;
    hasLoggedRef.current = true;

    const run = async () => {
      try {
        if (!currentCompany?.id) return;

        await fetchMembers?.();
        console.group(`Equipe - ${currentCompany?.name ?? currentCompany.id}`);
        if (members?.length) {
          console.table(
            (members as any[]).map((m: any) => ({
              name: m.profile?.full_name ?? m.profile?.email ?? m.user_id,
              email: m.profile?.email,
              role: m.role,
            }))
          );
        } else {
          console.info('Aucun membre trouvé');
        }

        if (userRole === 'admin') {
          await fetchInvitations?.();
          const pending = (invitations || []).filter((inv: any) => inv.status === 'pending');
          if (pending.length) {
            console.table(
              pending.map((inv: any) => ({
                email: inv.email,
                role: inv.role,
                expires_at: inv.expires_at,
              }))
            );
          } else {
            console.info('Aucune invitation en attente');
          }
        }

        await logActivity?.('page_view', 'page', 'team_management', {
          member_count: members?.length || 0,
          has_pending_invitations: (invitations || []).some((inv: any) => inv.status === 'pending'),
        });
        console.groupEnd();
      } catch (e) {
        console.error("Erreur lors de l'affichage des membres:", e);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  return (
    <MainLayout title="Gestion d'équipe">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gestion d'équipe</h1>
          <p className="text-muted-foreground">
            Gérez les membres de votre équipe et envoyez des invitations
          </p>
        </div>
        <CompanySelector />
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/20 transition-transform duration-200 hover:scale-105">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
                    Gestion d'équipe
                  </p>
                  <p className="text-xl font-bold text-green-800 dark:text-green-200">
                    Actions d'équipe
                  </p>
                </div>
              </div>
              <TeamInvitation />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Invitez de nouveaux membres à rejoindre votre équipe en envoyant une invitation par e-mail.
              Les membres invités recevront un lien pour accepter l'invitation et rejoindre l'entreprise.
            </p>
          </CardContent>
        </Card>

        <PendingInvitationsList />
        <TeamMembersList />
      </div>
    </div>
    </MainLayout>
  );
};